import React from 'react';
import { Avatar } from '@mui/material';

// Standard size variants in pixels
const SIZE_VARIANTS = {
  xs: 24,
  sm: 32,
  md: 40,  // default
  lg: 56,
  xl: 80,
  xxl: 100
};

/**
 * RarityAvatar - A standardized avatar component for displaying item images with consistent styling
 * 
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Alt text for accessibility
 * @param {number|'xs'|'sm'|'md'|'lg'|'xl'|'xxl'} [props.size='md'] - Size in pixels or size variant
 * @param {string} [props.variant='rounded'] - Avatar variant (rounded, circular, square)
 * @param {Object} [props.sx] - Additional styles
 * @param {React.Ref} ref - Forwarded ref
 */
const RarityAvatar = React.forwardRef(({ 
  src, 
  alt, 
  size = 'md', 
  variant = 'rounded',
  sx = {},
  ...props 
}, ref) => {
  // Debug logging
  React.useEffect(() => {
    if (src && src.includes('truffle')) {
      console.log('RarityAvatar - Loading image:', src);
      const img = new Image();
      img.onload = () => console.log('Image loaded successfully');
      img.onerror = (e) => console.error('Failed to load image:', src, e);
      img.src = src;
    }
  }, [src]);
  // Fixed size for all avatars
  
  // Check if the image is the CFB icon to apply specific styling
  const isCFB = src && (src.includes('icon_CFB.svg') || alt === '$CFB');
  
  // Special handling for CFB icon
  const cfbStyles = isCFB ? {
    '& .MuiAvatar-img': {
      padding: '8px', // Add some padding for the CFB icon
      boxSizing: 'border-box',
      backgroundColor: 'transparent !important',
      backgroundImage: 'none !important'
    }
  } : {};
  
  const avatarSx = {
    width: typeof size === 'number' ? size : SIZE_VARIANTS[size] || SIZE_VARIANTS.md,
    height: typeof size === 'number' ? size : SIZE_VARIANTS[size] || SIZE_VARIANTS.md,
    backgroundColor: 'transparent',
    '& img': {
      objectFit: 'contain',
      width: '100%',
      height: '100%',
    },
    ...cfbStyles,
    ...sx,
  };

  const imgStyle = {
    maxHeight: '100%',
    backgroundColor: 'transparent',
    objectFit: isCFB ? 'contain' : 'cover',
    objectPosition: 'center',
    display: 'block',
    padding: 0,
    margin: 0,
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    ...(isCFB && {
      padding: '8px',
      boxSizing: 'border-box',
    })
  };

  // Removed debug border for truffle images

  return (
    <Avatar
      ref={ref}
      src={src}
      alt={alt}
      variant={variant}
      sx={avatarSx}
      imgProps={{
        onError: (e) => {
          console.error(`Failed to load image: ${src}`, e);
          e.target.style.display = 'none';
        },
        style: imgStyle
      }}
      {...props}
    >
      {!src && alt && alt.charAt(0).toUpperCase()}
    </Avatar>
  );
});

// Attach size variants to the component for easier access
RarityAvatar.sizes = SIZE_VARIANTS;

export { RarityAvatar };
export default RarityAvatar;
